import { motion } from 'framer-motion';

export type PracticeFilter = 'all' | 'government' | 'history' | 'civics';

export type FilterCounts = Record<PracticeFilter, number>;

export interface FilterTab {
  value: PracticeFilter;
  label: string;
  matches: (category: string) => boolean;
}

const lower = (s: string) => (s ?? '').toLowerCase();

export const PRACTICE_TABS: FilterTab[] = [
  { value: 'all', label: 'All', matches: () => true },
  {
    value: 'government',
    label: 'Government',
    matches: (c) => lower(c).includes('government'),
  },
  {
    value: 'history',
    label: 'History',
    matches: (c) => lower(c).includes('history'),
  },
  {
    value: 'civics',
    label: 'Civics',
    matches: (c) => /civic|geography|symbol|holiday/i.test(c),
  },
];

interface CategoryFilterProps {
  filter: PracticeFilter;
  counts: FilterCounts;
  onChange: (filter: PracticeFilter) => void;
  disabled?: boolean;
}

export default function CategoryFilter({
  filter,
  counts,
  onChange,
  disabled = false,
}: CategoryFilterProps) {
  return (
    <div
      role="tablist"
      aria-label="Filter questions by category"
      className="flex flex-wrap gap-x-1 gap-y-2 border-b border-border/60"
    >
      {PRACTICE_TABS.map(({ value, label }) => {
        const active = filter === value;
        const count = counts[value];
        return (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(value)}
            disabled={disabled}
            className={`relative inline-flex items-baseline gap-1.5 px-4 py-2.5 font-sans text-sm transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${
              active
                ? 'text-primary font-semibold'
                : 'text-text-muted hover:text-text-secondary font-medium'
            }`}
          >
            <span>{label}</span>
            <span
              className={`text-[11px] font-medium tabular-nums ${
                active ? 'text-primary/70' : 'text-text-muted/70'
              }`}
            >
              {count}
            </span>
            {active && (
              <motion.span
                layoutId="category-filter-underline"
                className="absolute -bottom-[1px] left-3 right-3 h-[2px] bg-primary rounded-full"
                transition={{ type: 'spring', stiffness: 380, damping: 30 }}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}
